import { plainToClass } from 'class-transformer';
import { Injectable } from '@angular/core';

import { CollectApiServiceBase } from './collect-api-base.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ProgressIndicatorService } from './progress-indicator.service';
import { Folder } from '../models/site-content/folder.model';
import { FolderPermissionExpression } from '../models/site-metadata/folder-permission-expression.model';
import { FolderRolePermissionExpression } from '../models/site-metadata/folder-role-permission-expression.model';
import { FolderRoleMember } from '../models/site-metadata/folder-role-member.model';
import { AppInjector } from '../../app.module';
import { CurrentSiteService } from './current-site.service';
import { Role } from '../models/role.model';
import { FolderFormInstanceTree } from '../models/folder-form-instance-tree.model';
import { Undo } from '../models/site-metadata/Undo.model';
import { TestServiceMethodsData, CallServiceMethod } from './call-service-method.model';
import { SiteLevelRoleMember } from '../models/site-level-role-member.model';
//import { RoleGroupMember } from '../models/role-group-member.model';

@Injectable({
    providedIn: 'root'
})
export class FolderPermissionService extends CollectApiServiceBase<FolderPermissionExpression> {
    public constructor(http: HttpClient, progressIndicatorService: ProgressIndicatorService) {
        super(http, progressIndicatorService, environment.apiUrl, 'folderPermission', FolderPermissionExpression)
    }

    public formatResponse(data: FolderPermissionExpression): FolderPermissionExpression {
        let obj = this.loadFolderPermissionExpression(data);//plainToClass(FolderPermissionExpression, data);
        return (obj);
    }

    // Test service method.
    //
    // Note:  the idea here is that, given a single Id, many services could perform a test of many/most
    //        of its methods.  Here the Id is defined as a folder Id, but for another service it could
    //        be a data collection Id, perhaps a workflow Id.
    //public testServiceUsingObject(folderId: number): void {
    public testServiceUsingObjects(objectIds: number[]): void {
        // Require more than one folder Id.
        if (objectIds.length < 3)
            throw `FolderPermissionService.testServiceUsingObjects():  at least a site Id and two folder Ids are required to test this service.`;

        // Define test-related constants.
        const testMemberType = 'Group';
        const testMember = 'AGY-BRD';

        // Define a hash to contain the result of prior calls, indexed by method name.
        //let resultOfPriorCalls = {};
        let testServiceMethodsData: TestServiceMethodsData = new TestServiceMethodsData();
        let dataCollectionId: number = objectIds[0];
        let folderId: number = objectIds[1];
        let folderId2: number = objectIds[2];

        // Define data that characterizes the methods to be tested.
        let serviceCallExpressions: CallServiceMethod[] =
            [
                new CallServiceMethod(
                    {
                        methodName: 'createStandardPermissionExpressions',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = service.createStandardPermissionExpressions(dataCollectionId);
                            return promise;
                        }
                    }
                ),                
                new CallServiceMethod(
                    {
                        methodName: 'getSitePermissionExpressions',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = service.getSitePermissionExpressions(dataCollectionId);
                            return promise;
                        }
                    }
                ),
                new CallServiceMethod(
                    {
                        methodName: 'assignDefaultPermissionExpressionToAllFolders',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = service.assignDefaultPermissionExpressionToAllFolders(dataCollectionId);
                            return promise;
                        }
                    }
                ),

                new CallServiceMethod(
                    {
                        methodName: 'createFolderPermissionExpression',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderPermissionExpression = new FolderPermissionExpression();
                            folderPermissionExpression.dataCollectionId = dataCollectionId;
                            folderPermissionExpression.name = 'Permission Expression 1';
                            folderPermissionExpression.tag = 'PE_1';
                            folderPermissionExpression.colorRedValue = 1;
                            folderPermissionExpression.colorGreenValue = 2;
                            folderPermissionExpression.colorBlueValue = 3;
                            folderPermissionExpression.colorAlphaValue = 4;
                            folderPermissionExpression.canBeSelectedForFolders = true;
                            folderPermissionExpression.isDefaultPermissionExpressionFlag = false;
                            folderPermissionExpression.isBuiltInPermissionExpressionFlag = false;
                            //let promise = service.createFolderPermissionExpression(folderId2, folderPermissionExpression);
                            let promise = service.createFolderPermissionExpression(0, folderPermissionExpression);
                            return promise;
                        }
                    }),
                new CallServiceMethod(
                    {
                        methodName: 'updateFolderPermissionExpression',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderExpressionId: number = testServiceMethodsData.resultOfPriorCalls['createFolderPermissionExpression']['id'];

                            let folderPermissionExpression = new FolderPermissionExpression();
                            folderPermissionExpression.id = folderExpressionId;
                            folderPermissionExpression.name = 'Permission Expression 1B';
                            folderPermissionExpression.tag = 'PE_1B';
                            folderPermissionExpression.colorRedValue = 11;
                            folderPermissionExpression.colorGreenValue = 12;
                            folderPermissionExpression.colorBlueValue = 13;
                            folderPermissionExpression.colorAlphaValue = 14;

                            folderPermissionExpression.canBeSelectedForFolders = false;
                            folderPermissionExpression.isDefaultPermissionExpressionFlag = true;
                            folderPermissionExpression.isBuiltInPermissionExpressionFlag = false;
                            let promise = service.updateFolderPermissionExpression(folderPermissionExpression);
                            return promise;
                        }
                    }),
                new CallServiceMethod(
                    {
                        methodName: 'assignFolderExpressionToFolder1',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderExpressionId: number = testServiceMethodsData.resultOfPriorCalls['createFolderPermissionExpression']['id'];
                            let promise = service.assignFolderExpressionToFolder(folderExpressionId, folderId, true, true);
                            return promise;
                        }
                    }),
                // Disabling the logic to remove a permission expression from a folder.
                /*
                new CallServiceMethod(
                    {
                        methodName: 'deleteFolderPermissionExpression',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = service.deleteFolderPermissionExpression(folderId2);
                            return promise;
                        }
                    }),
                */

                new CallServiceMethod(
                    {
                        methodName: 'addFolderRolePermission',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderExpressionId: number = testServiceMethodsData.resultOfPriorCalls['createFolderPermissionExpression']['id'];
                            let currentSiteService = AppInjector.get(CurrentSiteService);
                            let role: Role = currentSiteService.Site.roles[0];
                            let rolePermissionExpression: FolderRolePermissionExpression = new FolderRolePermissionExpression(folderExpressionId, role.id);
                            rolePermissionExpression.canViewFolder = true;
                            rolePermissionExpression.canCreateFolder = false;
                            rolePermissionExpression.canCreateForm = true;
                            rolePermissionExpression.canDeleteForm = true;
                            rolePermissionExpression.canGrantPermission = false;
                            rolePermissionExpression.canManageRoleMembers = true;

                            let promise = service.addFolderRolePermission(rolePermissionExpression);
                            return promise;
                        }
                    }),

                new CallServiceMethod(
                    {
                        methodName: 'addFolderRoleMember',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderPermissionExpression: FolderPermissionExpression = testServiceMethodsData.resultOfPriorCalls['addFolderRolePermission'];
                            if ((folderPermissionExpression == null) || (folderPermissionExpression.folderRolePermissionExpressions == null) || (folderPermissionExpression.folderRolePermissionExpressions.length == 0))
                                throw `FolderPermissionService.testServiceUsingObject():  could not get the recently added folder role permission expression object`;
                            let folderRolePermissionExpressionId: number = folderPermissionExpression.folderRolePermissionExpressions[0].id;
                            let folderRoleMember: FolderRoleMember = new FolderRoleMember(folderId, folderRolePermissionExpressionId, testMemberType, testMember, testMember);
                            let promise = service.addFolderRoleMember(folderRoleMember);
                            return promise;
                        }
                    }),

                new CallServiceMethod(
                    {
                        methodName: 'getSiteFoldersWithPermissions',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = service.getSiteFoldersWithPermissions(dataCollectionId);
                            return promise;
                        }
                    }),

                new CallServiceMethod(
                    {
                        methodName: 'removeFolderRoleMember',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderPermissionExpression: FolderPermissionExpression = testServiceMethodsData.resultOfPriorCalls['addFolderRolePermission'];
                            if ((folderPermissionExpression == null) || (folderPermissionExpression.folderRolePermissionExpressions == null) || (folderPermissionExpression.folderRolePermissionExpressions.length == 0))
                                throw `FolderPermissionService.testServiceUsingObject():  could not get the recently added folder role permission expression object`;
                            let folderRolePermissionExpressionId: number = folderPermissionExpression.folderRolePermissionExpressions[0].id;
                            let folderRoleMember: FolderRoleMember = new FolderRoleMember(folderId, folderRolePermissionExpressionId, testMemberType, testMember, testMember);
                            let promise = service.removeFolderRoleMember(folderRoleMember);
                            return promise;
                        }
                    }),

                new CallServiceMethod(
                    {
                        methodName: 'removeFolderRolePermission',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderExpressionId: number = testServiceMethodsData.resultOfPriorCalls['createFolderPermissionExpression']['id'];
                            let folderPermissionExpression: FolderPermissionExpression = testServiceMethodsData.resultOfPriorCalls['addFolderRolePermission'];
                            if ((folderPermissionExpression == null) || (folderPermissionExpression.folderRolePermissionExpressions == null) || (folderPermissionExpression.folderRolePermissionExpressions.length == 0))
                                throw `FolderPermissionService.testServiceUsingObject():  could not get the recently added folder role permission expression object`;
                            let roleId: number = folderPermissionExpression.folderRolePermissionExpressions[0].roleId;
                            let rolePermissionExpression: FolderRolePermissionExpression = new FolderRolePermissionExpression(folderExpressionId, roleId);
                            let promise = service.removeFolderRolePermission(rolePermissionExpression);
                            return promise;
                        }
                    }),

                //new CallServiceMethod(
                //    {
                //        methodName: 'updateFolderRolePermission',
                //        repeatMethodNumTimes: 0,
                //        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                //            let folderExpressionId: number = testServiceMethodsData.resultOfPriorCalls['createFolderPermissionExpression']['id'];
                //            let folderPermissionExpression: FolderPermissionExpression = testServiceMethodsData.resultOfPriorCalls['addFolderRolePermission'];
                //            if ((folderPermissionExpression == null) || (folderPermissionExpression.folderRolePermissionExpressions == null) || (folderPermissionExpression.folderRolePermissionExpressions.length == 0))
                //                throw `FolderPermissionService.testServiceUsingObject():  could not get the recently added folder role permission expression object`;
                //            let roleId: number = folderPermissionExpression.folderRolePermissionExpressions[0].roleId;
                //            let rolePermissionExpression: FolderRolePermissionExpression = new FolderRolePermissionExpression(folderExpressionId, roleId);
                //            let promise = service.updateFolderRolePermission(rolePermissionExpression);
                //            return promise;
                //        }
                //    }),

                new CallServiceMethod(
                    {
                        methodName: 'getSiteExecutableUndoActions',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = service.getSiteExecutableUndoActions(dataCollectionId, Undo.appArea_FolderPermission);
                            return promise;
                        }
                    }
                ),
                new CallServiceMethod(
                    {
                        methodName: 'undoLastPermissionApplied1',
                        repeatMethodNumTimes: 6, // So this method will run a total of seven times.
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = service.undoLastPermissionApplied(dataCollectionId);
                            return promise;
                        }
                    })
            ];
        let serviceCallExpressions2: CallServiceMethod[] =
            [
                new CallServiceMethod(
                    {
                        methodName: 'createFolderPermissionExpression',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderPermissionExpression = new FolderPermissionExpression();
                            folderPermissionExpression.name = 'Permission Expression 2';
                            folderPermissionExpression.tag = 'PE_2';
                            folderPermissionExpression.colorRedValue = 110;
                            folderPermissionExpression.colorGreenValue = 120;
                            folderPermissionExpression.colorBlueValue = 130;
                            folderPermissionExpression.colorAlphaValue = 140;
                            folderPermissionExpression.canBeSelectedForFolders = true;
                            folderPermissionExpression.isDefaultPermissionExpressionFlag = false;
                            folderPermissionExpression.isBuiltInPermissionExpressionFlag = false;
                            let promise = service.createFolderPermissionExpression(folderId, folderPermissionExpression);
                            return promise;
                        }
                    }),
                new CallServiceMethod(
                    {
                        methodName: 'assignFolderExpressionToFolder1',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderExpressionId: number = testServiceMethodsData.resultOfPriorCalls['createFolderPermissionExpression']['id'];
                            let promise = service.assignFolderExpressionToFolder(folderExpressionId, folderId2);
                            return promise;
                        }
                    }),
                new CallServiceMethod(
                    {
                        methodName: 'deleteFolderPermissionExpression',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = service.deleteFolderPermissionExpression(folderId);
                            return promise;
                        }
                    }),

                new CallServiceMethod(
                    {
                        methodName: 'undoLastPermissionApplied1',
                        repeatMethodNumTimes: 3, // So this method will run a total of four times.
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = service.undoLastPermissionApplied(dataCollectionId);
                            return promise;
                        }
                    })
            ];
        let serviceCallExpressions3: CallServiceMethod[] =
            [
                new CallServiceMethod(
                    {
                        methodName: 'createFolderPermissionExpression',
                        repeatMethodNumTimes: 0,
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let folderPermissionExpression = new FolderPermissionExpression();
                            folderPermissionExpression.name = 'Permission Expression 3';
                            folderPermissionExpression.tag = 'PE_3';
                            folderPermissionExpression.colorRedValue = 51;
                            folderPermissionExpression.colorGreenValue = 52;
                            folderPermissionExpression.colorBlueValue = 53;
                            folderPermissionExpression.colorAlphaValue = 54;
                            folderPermissionExpression.canBeSelectedForFolders = true;
                            folderPermissionExpression.isDefaultPermissionExpressionFlag = false;
                            folderPermissionExpression.isBuiltInPermissionExpressionFlag = false;
                            let promise = service.createFolderPermissionExpression(folderId, folderPermissionExpression);
                            return promise;
                        }
                    }),

                new CallServiceMethod(
                    {
                        methodName: 'undoLastPermissionApplied',
                        repeatMethodNumTimes: 0, // So this method will run a total of one time.
                        callExpr: (service: FolderPermissionService, testServiceMethodsData: TestServiceMethodsData) => {
                            let promise = service.undoLastPermissionApplied(dataCollectionId);
                            return promise;
                        }
                    })
            ];

        // Test service methods.
        //FolderPermissionService.testServiceMethods(this, serviceCallExpressions, testServiceMethodsData); // methodInfo);
        CallServiceMethod.testServiceMethods('FolderPermissionService', this, serviceCallExpressions, testServiceMethodsData);
        //FolderPermissionService.testServiceMethods(this, serviceCallExpressions3, testServiceMethodsData); // methodInfo);
    }

    /*
    //private static testServiceMethods(service: FolderPermissionService, callServiceMethods: CallServiceMethod[], testServiceMethodsData: TestServiceMethodsData, methodIndex: number = 0, methodIterationIndex: number = 0): void {
    public static testServiceMethods(service: object, callServiceMethods: CallServiceMethod[], testServiceMethodsData: TestServiceMethodsData, methodIndex: number = 0, methodIterationIndex: number = 0): void {
        // Define the logic to call service methods in succession.
        //
        // Call service methods in succession until all have been called or an error occurs.

        let nextMethodInfo: CallServiceMethod = callServiceMethods[methodIndex];
        console.log(`FolderPermissionService.testServiceMethods():  calling service method ${nextMethodInfo.methodName} ...`);

        let promise: Promise<object> = nextMethodInfo.callExpr(service, testServiceMethodsData);
        promise.then(response => {
            testServiceMethodsData.resultOfPriorCalls[nextMethodInfo.methodName] = response;
            if ((methodIterationIndex < nextMethodInfo.repeatMethodNumTimes) || (methodIndex < callServiceMethods.length - 1)) {
                let nextMethodName: string = methodIterationIndex < nextMethodInfo.repeatMethodNumTimes ?
                    callServiceMethods[methodIndex].methodName :
                    callServiceMethods[methodIndex + 1].methodName;
                if (methodIterationIndex < nextMethodInfo.repeatMethodNumTimes)
                    FolderPermissionService.testServiceMethods(service, callServiceMethods, testServiceMethodsData, methodIndex, methodIterationIndex + 1);
                else if (methodIndex < callServiceMethods.length - 1)
                    FolderPermissionService.testServiceMethods(service, callServiceMethods, testServiceMethodsData, methodIndex + 1);
                else
                    throw `FolderPermissionService.testserviceMethods():  could not find next method '${nextMethodName}'.`;
            } else {
                console.log(`FolderPermissionService.testserviceMethods():  completed testing without error!`);
            }
        });
    }
    */

    // Service methods.
    public getSiteFoldersWithPermissions(dataCollectionId: number): Promise<FolderFormInstanceTree> {
        let url = `${this.url}/${this.endpoint}/getSiteFoldersWithPermissions/${dataCollectionId}`;
        return this.http.get<FolderFormInstanceTree>(url)
            .toPromise()
            .then(response => {
                let result: FolderFormInstanceTree = plainToClass(FolderFormInstanceTree, response);
                return result;
            });
    }

    public getSiteFolderWithPermissions(folderId: number): Promise<FolderFormInstanceTree> {
        let url = `${this.url}/${this.endpoint}/getSiteFolderWithPermissions/${folderId}`;
        return this.http.get<FolderFormInstanceTree>(url)
            .toPromise()
            .then(response => {
                let result: FolderFormInstanceTree = plainToClass(FolderFormInstanceTree, response);
                return result;
            });
    }

    public createFolderPermissionExpression(folderId: number, folderPermissionExpression: FolderPermissionExpression): Promise<FolderPermissionExpression> {
        let url = `${this.url}/${this.endpoint}/createFolderPermissionExpression/${folderId}`;
        return this.http.post<FolderPermissionExpression>(url, folderPermissionExpression)
            .toPromise()
            .then(response => {
                let result: FolderPermissionExpression = this.formatResponse(response);
                return result;
            });
    }
    public updateFolderPermissionExpression(folderPermissionExpression: FolderPermissionExpression): Promise<FolderPermissionExpression> {
        let url = `${this.url}/${this.endpoint}/updateFolderPermissionExpression`;
        return this.http.put<FolderPermissionExpression>(url, folderPermissionExpression)
            .toPromise()
            .then(response => {
                let result: FolderPermissionExpression = this.formatResponse(response);
                return result;
            });
    }
    public deleteFolderPermissionExpression(folderId: number): Promise<Folder> {
        let url = `${this.url}/${this.endpoint}/deleteFolderPermissionExpression/${folderId}`;
        return this.http.get<Folder>(url)
            .toPromise()
            .then(response => {
                let result: Folder = plainToClass(Folder, response);
                return result;
            });
    }
    public deleteFolderPermissionExpressionById(permissionExpressionId: number): Promise<Folder> {
        let url = `${this.url}/${this.endpoint}/deleteFolderPermissionExpressionById/${permissionExpressionId}`;
        return this.http.get<Folder>(url)
            .toPromise()
            .then(response => {
                let result: Folder = plainToClass(Folder, response);
                return result;
            });
    }
    public assignFolderExpressionToFolder(folderExpressionId: number, folderId: number, applyToDescendantsFlag: boolean = true, lockPermissionExpression: boolean = false): Promise<FolderPermissionExpression> {
        let url = `${this.url}/${this.endpoint}/assignFolderExpressionToFolder/${folderExpressionId}/${folderId}/${applyToDescendantsFlag}/${lockPermissionExpression}`;
        return this.http.get<FolderPermissionExpression>(url)
            .toPromise()
            .then(response => {
                let result: FolderPermissionExpression = this.formatResponse(response);
                return result;
            });
    }

    public addFolderRolePermission(rolePermissionExpression: FolderRolePermissionExpression): Promise<FolderPermissionExpression> {
        let url = `${this.url}/${this.endpoint}/addFolderRolePermission`;
        return this.http.put<FolderPermissionExpression>(url, rolePermissionExpression)
            .toPromise()
            .then(response => {
                let result: FolderPermissionExpression = this.formatResponse(response);
                return result;
            })
            .catch(this.handleError);
    }
    public removeFolderRolePermission(rolePermissionExpression: FolderRolePermissionExpression): Promise<FolderPermissionExpression> {
        let url = `${this.url}/${this.endpoint}/removeFolderRolePermission`;
        return this.http.put<FolderPermissionExpression>(url, rolePermissionExpression)
            .toPromise()
            .then(response => {
                let result: FolderPermissionExpression = this.formatResponse(response);
                return result;
            })
            .catch(this.handleError);
    }
    public updateFolderRolePermission(rolePermissionExpression: FolderRolePermissionExpression): Promise<FolderRolePermissionExpression> {
        let url = `${this.url}/${this.endpoint}/updateFolderRolePermission`;
        return this.http.put<FolderPermissionExpression>(url, rolePermissionExpression)
            .toPromise()
            .then(response => {
                let result: FolderPermissionExpression = this.formatResponse(response);
                return result;
            })
            .catch(this.handleError);
    }

    public addFolderRoleMember(roleMember: FolderRoleMember): Promise<FolderRolePermissionExpression> {
        let url = `${this.url}/${this.endpoint}/addFolderRoleMember`;
        return this.http.put<FolderRolePermissionExpression>(url, roleMember)
            .toPromise()
            .then(response => {
                let result = plainToClass(FolderRolePermissionExpression, response);
                return result;
            })
            .catch(this.handleError);
    }
    public removeFolderRoleMember(roleMember: FolderRoleMember): Promise<FolderRolePermissionExpression> {
        let url = `${this.url}/${this.endpoint}/removeFolderRoleMember`;
        return this.http.put<FolderRolePermissionExpression>(url, roleMember)
            .toPromise()
            .then(response => {
                let result = plainToClass(FolderRolePermissionExpression, response);
                return result;
            })
            .catch(this.handleError);
    }

    public undoLastPermissionApplied(dataCollectionId: number): Promise<FolderFormInstanceTree> {
        let url = `${this.url}/${this.endpoint}/undoLastPermissionApplied/${dataCollectionId}`;
        return this.http.get<FolderFormInstanceTree>(url)
            .toPromise()
            .then(response => {
                let result: FolderFormInstanceTree = plainToClass(FolderFormInstanceTree, response);
                return result;
            });
    }
    public redoLastPermissionUndone(dataCollectionId: number): Promise<FolderFormInstanceTree> {
        let url = `${this.url}/${this.endpoint}/redoLastPermissionUndone/${dataCollectionId}`;
        return this.http.get<FolderFormInstanceTree>(url)
            .toPromise()
            .then(response => {
                let result: FolderFormInstanceTree = plainToClass(FolderFormInstanceTree, response);
                return result;
            });
    }

    public createStandardPermissionExpressions(dataCollectionId: number): Promise<FolderPermissionExpression[]> {
        let url = `${this.url}/${this.endpoint}/createStandardPermissionExpressions/${dataCollectionId}`;
        return this.http.get<FolderPermissionExpression[]>(url)
            .toPromise()
            .then(response => {
                let result: FolderPermissionExpression[] = [];
                if ((response != null) && (response.length > 0)) {
                    for (let index: number = 0; index < response.length; index++) {
                        let responseObject: FolderPermissionExpression = response[index];
                        let resultObject: FolderPermissionExpression = this.formatResponse(responseObject);
                        result.push(resultObject);
                    }
                }
                return result;
            });
    }

    public getSitePermissionExpressions(dataCollectionId: number): Promise<FolderPermissionExpression[]> {
        let url = `${this.url}/${this.endpoint}/getSitePermissionExpressions/${dataCollectionId}`;
        return this.http.get<FolderPermissionExpression[]>(url)
            .toPromise()
            .then(response => {
                let result: FolderPermissionExpression[] = [];
                if ((response != null) && (response.length > 0)) {
                    for (let index: number = 0; index < response.length; index++) {
                        let responseObject: FolderPermissionExpression = response[index];
                        let resultObject: FolderPermissionExpression = this.formatResponse(responseObject);
                        result.push(resultObject);
                    }
                }
                return result;
            });
    }

    public assignDefaultPermissionExpressionToAllFolders(dataCollectionId: number): Promise<FolderFormInstanceTree> {
        let url = `${this.url}/${this.endpoint}/assignDefaultPermissionExpressionToAllFolders/${dataCollectionId}`;
        return this.http.get<FolderFormInstanceTree>(url)
            .toPromise()
            .then(response => {
                let result: FolderFormInstanceTree = plainToClass(FolderFormInstanceTree, response);
                return result;
            });
    }

    public getSiteExecutableUndoActions(dataCollectionId: number, applicationArea: string): Promise<Undo[]> {
        let url = `${this.url}/${this.endpoint}/getSiteExecutableUndoActions/${dataCollectionId}/${applicationArea}`;
        return this.http.get<Undo[]>(url)
            .toPromise()
            .then(response => {
                let result: Undo[] = [];
                if ((response != null) && (response.length > 0)) {
                    for (let index: number = 0; index < response.length; index++) {
                        let responseObject: Undo = response[index];
                        let resultObject: Undo = plainToClass(Undo, responseObject);
                        result.push(resultObject);
                    }
                }
                return result;
            });
    }

    // Define end points for managing roles according to the new permissions model.
    public addRole(role: Role): Promise<Role> {
        let url = `${this.url}/${this.endpoint}/addRole`;
        return this.http.post<Role>(url, role)
            .toPromise()
            .then(response => {
                let result: Role = plainToClass(Role, response);
                return result;
            });
    }

    public updateRole(role: Role): Promise<Role> {
        let url = `${this.url}/${this.endpoint}/updateRole`;
        return this.http.put<Role>(url, role)
            .toPromise()
            .then(response => {
                let result: Role = plainToClass(Role, response);
                return result;
            });
    }

    public deleteRole(role: Role): Promise<Role> {
        let url = `${this.url}/${this.endpoint}/deleteRole`;
        return this.http.post<Role>(url, role)
            .toPromise()
            .then(response => {
                let result: Role = plainToClass(Role, response);
                return result;
            });
    }

    // Define end points for managing site-level role members.
    // Note:  I decided to create these end points so that this
    //        code could evolve independently of the existing
    //        role member management methods implemented in
    //        class RoleService, if necessary.
    public getSiteLevelRoleMembers(roleId: number): Promise<SiteLevelRoleMember[]> {
        let url = `${this.url}/${this.endpoint}/getSiteLevelRoleMembers/${roleId}`; 
        return this.http.get<SiteLevelRoleMember[]>(url)
            .toPromise()
            .then(response => {
                let result: SiteLevelRoleMember[] = [];

                if (response != null) {
                    for (let index: number = 0; index < response.length; index++) {
                        let roleMember: SiteLevelRoleMember = plainToClass(SiteLevelRoleMember, response[index]);
                        result.push(roleMember);
                    }
                }
                
                return result;
            });
    }

    public addSiteLevelRoleMember(dataCollectionId: number, roleGroupMember: SiteLevelRoleMember): Promise<SiteLevelRoleMember> {
        let url = `${this.url}/${this.endpoint}/addSiteLevelRoleMember/${dataCollectionId}`; 
        return this.http.post<SiteLevelRoleMember>(url, roleGroupMember)
            .toPromise()
            .then(response => {
                let result: SiteLevelRoleMember = plainToClass(SiteLevelRoleMember, response);
                return result;
            });
    }

    public deleteSiteLevelRoleMember(dataCollectionId: number, roleGroupMember: SiteLevelRoleMember): Promise<SiteLevelRoleMember> {
        let url = `${this.url}/${this.endpoint}/deleteSiteLevelRoleMember/${dataCollectionId}`;
        return this.http.post<SiteLevelRoleMember>(url, roleGroupMember)
            .toPromise()
            .then(response => {
                let result: SiteLevelRoleMember = plainToClass(SiteLevelRoleMember, response);
                return result;
            });
    }

    // Helper methods.
    private loadFolderPermissionExpression(responseObject: FolderPermissionExpression): FolderPermissionExpression {
        let resultObject: FolderPermissionExpression = plainToClass(FolderPermissionExpression, responseObject);
        resultObject.loadRgbaBytesFromDelimitedValues();
        return resultObject;
    }
}
